import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import CommonImage from '@components/CommonImage';
import { toastError } from '@components/Toast';
import CustomMultipleSelect from '@components/CustomMultipleSelect';

import { useLazyDownloadOCSQuery } from '@features/costing-agent/api/costingAgentApi';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import ApiClient from '@lib/apiClient';

import { downloadBlobFileWithRTKQuery } from '@utils/Util';

import { BASE_URL } from '@/constants/constant';

import { useMixPanelTrakingEventMutation } from '@/api/commonApis';

import { COSTING_MIX_PANEL_EVENTS } from '../utils';

import AddFilesModal from './AddFilesModal';

const Header = ({
	onHistoryClick,
	onEyeClick,
	costingBrandInfo,
	baseQty,
	selectedFileList,
	setSelectedFileList,
	usedDocumentIds,
	isShowNitexTechpackLabel,
	hasFileChange,
	isShowDownload,
	versionId,
	onViewChange,
	activeView,
	productionIsloading,
	isProductionCostingDisabled = false,
	isProductDevelopment = false
}) => {
	const brandName = costingBrandInfo?.brandName;
	// const brandId = costingBrandInfo?.brandId;
	const defaultBrandName = 'Zalando';
	// const isBrandSelected = !!brandName;
	const [isAddFilesModalOpen, setIsAddFilesModalOpen] = useState(false);
	const [options, setOptions] = useState([]);
	const [files, setFiles] = useState({});
	const dispatch = useDispatch();
	const { id } = useParams();
	const [downloadOCS] = useLazyDownloadOCSQuery();
	const [mixPanelTrakingEvent] = useMixPanelTrakingEventMutation();
	const disableProductionCosting = productionIsloading || isProductionCostingDisabled;

	useEffect(() => {
		// if(usedDocumentIds?.length === 0 || Object.keys(files).length === 0) return;

		let tmpUserDocumentMap = {};
		for (let item in files) {
			let file = files[item];

			file = file.map((file) => {
				if (file.documentResponse?.id && usedDocumentIds?.includes(file.documentResponse.id)) {
					if (!tmpUserDocumentMap[item]) {
						tmpUserDocumentMap[item] = [file];
					} else {
						tmpUserDocumentMap[item] = [...tmpUserDocumentMap[item], file];
					}
				}
				return file;
			});
		}
		let tmpSelections = {};
		if (isShowNitexTechpackLabel) {
			tmpSelections.Internal = [
				{
					id: 'NITEX_TECHPACK',
					label: 'Nitex Techpack',
					clickAbleLink: null,
					hideCloseIcon: true
				}
			];
		}
		for (let key in tmpUserDocumentMap) {
			if (!tmpSelections[key]) {
				tmpSelections[key] = [
					{
						id: tmpUserDocumentMap[key][0].documentResponse.id,
						label: tmpUserDocumentMap[key][0].documentResponse.name,
						clickAbleLink: tmpUserDocumentMap[key][0].documentResponse.docUrl
					}
				];
			} else {
				tmpSelections[key] = [
					...tmpSelections[key],
					{
						id: tmpUserDocumentMap[key][0].documentResponse.id,
						label: tmpUserDocumentMap[key][0].documentResponse.name,
						clickAbleLink: tmpUserDocumentMap[key][0].documentResponse.docUrl
					}
				];
			}
		}

		setSelectedFileList(tmpSelections);
	}, [usedDocumentIds, files, isShowNitexTechpackLabel, setSelectedFileList]);

	useEffect(() => {
		if (id) {
			(async () => {
				await fetchProductFile();
			})();
		}
	}, [id]);

	const fetchProductFile = async () => {
		dispatch(openLoader());
		try {
			const { data } = await ApiClient.GET(
				`${BASE_URL}/entity-file/${id}/entityType/PRODUCT?isSpreedSheetNamesRequired=true`,
				'',
				true
			);
			setFiles(data);
			let tmpOptions = {};

			for (let item in data) {
				if (!['Pattern', 'Measurement', 'Techpack', 'Others'].includes(item)) continue;
				let file = data[item];

				file = file.map((file) => {
					return {
						id: file.documentResponse.id,
						label: file.documentResponse.name,
						clickAbleLink: file.documentResponse.docUrl,
						options:
							file?.documentResponse?.spreedSheetNames?.map((name) => ({
								id: name,
								label: name
							})) || undefined
					};
				});
				tmpOptions = { ...tmpOptions, [item]: file };
			}
			setOptions(tmpOptions);
			dispatch(closeLoader());
		} catch (error) {
			dispatch(closeLoader());
			if (error.message) {
				toastError(error.message);
			} else {
				toastError('Something went wrong! Please try again.');
			}
		}
	};

	const handleFileSelections = (key, file, sheetInfo) => {
		let tmpSelectedFiles = { ...selectedFileList };
		let obj = {
			id: file.id,
			label: file.label,
			clickAbleLink: file.clickAbleLink
		};
		if (sheetInfo) {
			obj.sheetName = sheetInfo.id;
		}
		if (!tmpSelectedFiles[key]) {
			tmpSelectedFiles[key] = [obj];
		} else {
			let isExist = tmpSelectedFiles[key].find((item) => item.id === file.id);
			if (isExist) {
				tmpSelectedFiles[key] = tmpSelectedFiles[key].filter((item) => item.id !== file.id);
			} else {
				tmpSelectedFiles[key] = [...tmpSelectedFiles[key], obj];
			}
		}
		if (tmpSelectedFiles[key] && tmpSelectedFiles[key].length === 0) {
			delete tmpSelectedFiles[key];
		}

		setSelectedFileList(tmpSelectedFiles);
		hasFileChange();
	};

	const onDownloadClick = async () => {
		dispatch(openLoader());
		try {
			const response = await downloadOCS({ productId: id, versionId }).unwrap();

			await downloadBlobFileWithRTKQuery(response, `ocs-costing-sheet-${id}-${versionId}.xlsx`);
			dispatch(closeLoader());
		} catch (error) {
			dispatch(closeLoader());
			toastError(error?.message || 'Failed to download the costing sheet.');
		}
	};

	return (
		<div className="costing-content-header">
			<div className="product-content d-flex align-items-center justify-content-between gap-2">
				<div className="d-flex">
					<CustomMultipleSelect
						toggleIsShowAddModal={setIsAddFilesModalOpen}
						optionMap={options}
						handleOption={handleFileSelections}
						selectedOptions={selectedFileList}
					/>
				</div>
				<div className="d-flex align-items-start gap-2"></div>

				<div className="brand-and-history transparent-select d-flex flex-wrap align-items-center justify-content-end gap-2">
					{!isProductDevelopment && (
						<button
							className={`button size32 ${activeView === 'market-context' ? '' : 'outline'}`}
							onClick={() => onViewChange('market-context')}
						>
							Market Context &amp; Target FOB
						</button>
					)}

					<button
						className={`button size32 ${disableProductionCosting ? 'cursor-none' : ''} ${activeView === 'production-costing' ? '' : 'outline'}`}
						onClick={() => {
							if (disableProductionCosting) return;

							mixPanelTrakingEvent({
								type: COSTING_MIX_PANEL_EVENTS.PRODUCTION_COST_CLICK,
								body: {}
							});
							onViewChange('production-costing');
						}}
					>
						Production Costing
						{productionIsloading && (
							<span className="rotated-icon icon-spin ml-1">
								<CommonImage src="/icons/Update-gray.svg" />
							</span>
						)}
					</button>
					{!isProductDevelopment && (
						<button
							className={`button size32 ${disableProductionCosting ? 'cursor-none' : ''} ${activeView === 'optimize-offer' ? '' : 'outline'}`}
							onClick={() => !disableProductionCosting && onViewChange('optimize-offer')}
						>
							Optimize &amp; Offer
						</button>
					)}

					{isShowDownload && (
						<button
							className="button text-only d-flex align-items-center justify-content-center"
							onClick={onDownloadClick}
						>
							<CommonImage
								src="/icons/download-dark-20.svg"
								alt="download"
								width={20}
								height={20}
							/>
						</button>
					)}
					<button
						className="button text-only d-flex align-items-center justify-content-center"
						onClick={onEyeClick}
					>
						<CommonImage src="/icons/eye.svg" alt="view" width={20} height={20} />
					</button>
					<p className="regular-14 gray_dark_01">Qty: {baseQty ? baseQty : 1000}</p>
					{}
					<p className="regular-14 gray_dark_01">{brandName || defaultBrandName}</p>
					{/* <div className="form-group mb-0 w-auto">
						<CustomDropdown
							title="Project type*"
							onItemClick={() => {}}
							selectedItem={brandName || defaultBrandName}
							items={[{ id: brandId || '1', value: brandName || defaultBrandName }]}
							isShowSearch={false}
							isDisabled={!isBrandSelected}
						/>
					</div> */}
					<button
						className="button text-only d-flex align-items-center justify-content-center"
						onClick={onHistoryClick}
					>
						<CommonImage src="/icons/load.svg" alt="history" width={20} height={20} />
						<span className="ml-1 regular-14 gray_dark_01">History</span>
					</button>
				</div>
			</div>
			{isAddFilesModalOpen && (
				<AddFilesModal
					onCloseModal={() => setIsAddFilesModalOpen(false)}
					selectedFileList={selectedFileList}
					setSelectedFileList={setSelectedFileList}
					fetchProductFile={fetchProductFile}
				/>
			)}
		</div>
	);
};

export default Header;
